/**
 * Ten musical styles, one per level theme (LevelData.theme): the theme picks the mode,
 * the chord progressions, the instruments and the grooves; the level seed then picks the
 * key, the progression, the patterns and the melody (composer.ts).
 *
 * Patterns are 16-step strings, one character per sixteenth note of a bar.
 *  - grooves: `x` = hit.
 *  - bass: `r` root, `o` root an octave up, `f` fifth, `a` approach note (a step below the
 *    next chord's root), `.` holds the previous note.
 */
import { type BossKind, THEME_COUNT } from '@cube-rush/shared';
import type { Season } from '../../season/season';
import type { ScaleName } from './theory';

export type LeadInstrument =
  | 'pulse50'
  | 'pulse25'
  | 'pulse12'
  | 'softSaw'
  | 'triangle'
  | 'bell'
  | 'organ'
  | 'celesta';
export type ArpInstrument = 'pulse25' | 'pulse12' | 'softSaw' | 'triangle' | 'bell' | 'celesta';
export type BassInstrument = 'softSquare' | 'pulse25' | 'softSaw' | 'triangle';
/** chip: classic noise drums; soft: rounder and quieter; electro: clap and a deeper kick. */
export type DrumKit = 'chip' | 'soft' | 'electro';

export interface Groove {
  kick: string;
  snare: string;
  hat: string;
  /** Open hi-hats (offbeats), optional. */
  open?: string;
}

/** Broken chords: `order` lists chord tones (0 root, 1 third, 2 fifth, 3 octave), one every `rate` steps. */
export interface ArpPattern {
  rate: 1 | 2;
  order: readonly number[];
}

/** Offbeat chord stabs instead of a broken chord. */
export type Accompaniment = ArpPattern | 'stabs';

export interface MusicStyle {
  name: string;
  scale: ScaleName;
  /** Four chords per phrase, as scale degrees (0 = tonic). */
  progressions: readonly (readonly [number, number, number, number])[];
  lead: LeadInstrument;
  arp: ArpInstrument;
  bass: BassInstrument;
  kit: DrumKit;
  grooves: readonly Groove[];
  bassPatterns: readonly string[];
  accompaniments: readonly Accompaniment[];
  /** 0..1: how many of the weak-beat melody notes are played (fewer = airier tune). */
  leadDensity: number;
  /** Phrases start with a deep church bell instead of the crash cymbal (Halloween). */
  bells?: boolean;
  /** Sleigh bells play the hi-hat part, on the eighth notes (winter). */
  jingle?: boolean;
}

const FOUR: Groove = {
  kick: 'x...x...x...x...',
  snare: '....x.......x...',
  hat: '..x...x...x...x.',
};
const ROCK: Groove = {
  kick: 'x.......x.x.....',
  snare: '....x.......x...',
  hat: 'x.x.x.x.x.x.x.x.',
};
const DRIVE: Groove = {
  kick: 'x...x...x...x...',
  snare: '....x.......x...',
  hat: 'xxxxxxxxxxxxxxxx',
};
const HALF: Groove = {
  kick: 'x.........x.....',
  snare: '........x.......',
  hat: 'x.x.x.x.x.x.x.x.',
};
const BOUNCE: Groove = {
  kick: 'x..x..x...x.....',
  snare: '....x.......x...',
  hat: 'x.x.x.x.x.x.x.x.',
};
const SHUFFLE: Groove = {
  kick: 'x.....x.x.......',
  snare: '....x.......x..x',
  hat: 'x..x..x.x..x..x.',
};
const DISCO: Groove = {
  kick: 'x...x...x...x...',
  snare: '....x.......x...',
  hat: 'x...x...x...x...',
  open: '..x...x...x...x.',
};

const EIGHTHS = 'r.r.r.r.r.r.r.r.';
const OCTAVES = 'r.o.r.o.r.o.r.o.';
const SYNCOPATED = 'r..r..r...r.r.a.';
const PULSE = 'r.......r...r...';
const WALK = 'r...f...o...f.a.';
const ROOT_FIFTH = 'r..r..f.r..r..f.';

const UP: ArpPattern = { rate: 1, order: [0, 1, 2, 3] };
const UP_DOWN: ArpPattern = { rate: 1, order: [0, 1, 2, 3, 2, 1] };
const DOWN: ArpPattern = { rate: 1, order: [3, 2, 1, 0] };
const ALBERTI: ArpPattern = { rate: 2, order: [0, 2, 1, 2] };
const BROKEN: ArpPattern = { rate: 2, order: [0, 1, 2, 1, 3, 2, 1, 2] };

// Progressions (scale degrees, 0-based).
const POP: (readonly [number, number, number, number])[] = [
  [0, 4, 5, 3],
  [0, 5, 3, 4],
  [5, 3, 0, 4],
  [0, 3, 4, 3],
  [0, 3, 0, 4],
];
const BRIGHT: (readonly [number, number, number, number])[] = [
  [0, 2, 3, 4],
  [3, 4, 2, 5],
  [0, 3, 4, 4],
  [0, 4, 3, 4],
];
const MIXO: (readonly [number, number, number, number])[] = [
  [0, 6, 3, 0],
  [0, 3, 6, 3],
  [0, 6, 0, 4],
  [3, 6, 0, 0],
];
const LYDIAN: (readonly [number, number, number, number])[] = [
  [0, 1, 0, 1],
  [0, 1, 4, 3],
  [3, 1, 0, 4],
];
const DORIAN: (readonly [number, number, number, number])[] = [
  [0, 3, 0, 3],
  [0, 6, 3, 0],
  [0, 3, 6, 4],
];
const EPIC: (readonly [number, number, number, number])[] = [
  [0, 5, 2, 6],
  [0, 6, 5, 6],
  [0, 3, 6, 2],
  [5, 6, 0, 0],
];
const HEROIC: (readonly [number, number, number, number])[] = [
  [0, 3, 4, 0],
  [0, 5, 3, 4],
  [0, 5, 6, 4],
];

export const MUSIC_STYLES: readonly MusicStyle[] = [
  {
    name: 'sunny',
    scale: 'major',
    progressions: POP,
    lead: 'pulse50',
    arp: 'pulse25',
    bass: 'softSquare',
    kit: 'chip',
    grooves: [FOUR, DRIVE],
    bassPatterns: [EIGHTHS, OCTAVES],
    accompaniments: [UP, ALBERTI],
    leadDensity: 0.9,
  },
  {
    name: 'bubblegum',
    scale: 'major',
    progressions: [...POP, ...BRIGHT],
    lead: 'pulse12',
    arp: 'pulse25',
    bass: 'softSquare',
    kit: 'chip',
    grooves: [BOUNCE, DISCO],
    bassPatterns: [OCTAVES, ROOT_FIFTH],
    accompaniments: ['stabs', ALBERTI],
    leadDensity: 0.85,
  },
  {
    name: 'space',
    scale: 'lydian',
    progressions: LYDIAN,
    lead: 'triangle',
    arp: 'softSaw',
    bass: 'softSaw',
    kit: 'soft',
    grooves: [HALF],
    bassPatterns: [PULSE, WALK],
    accompaniments: [UP_DOWN, BROKEN],
    leadDensity: 0.7,
  },
  {
    name: 'jungle',
    scale: 'dorian',
    progressions: DORIAN,
    lead: 'bell',
    arp: 'triangle',
    bass: 'pulse25',
    kit: 'soft',
    grooves: [SHUFFLE, BOUNCE],
    bassPatterns: [SYNCOPATED, ROOT_FIFTH],
    accompaniments: [BROKEN, 'stabs'],
    leadDensity: 0.8,
  },
  {
    name: 'castle',
    scale: 'harmonicMinor',
    progressions: HEROIC,
    lead: 'pulse25',
    arp: 'pulse12',
    bass: 'softSquare',
    kit: 'chip',
    grooves: [ROCK, DRIVE],
    bassPatterns: [EIGHTHS, SYNCOPATED],
    accompaniments: [UP, DOWN],
    leadDensity: 0.9,
  },
  {
    name: 'ice',
    scale: 'major',
    progressions: BRIGHT,
    lead: 'bell',
    arp: 'triangle',
    bass: 'triangle',
    kit: 'soft',
    grooves: [HALF, FOUR],
    bassPatterns: [PULSE, WALK],
    accompaniments: [UP_DOWN, ALBERTI],
    leadDensity: 0.75,
  },
  {
    name: 'lava',
    scale: 'mixolydian',
    progressions: MIXO,
    lead: 'softSaw',
    arp: 'pulse25',
    bass: 'softSaw',
    kit: 'electro',
    grooves: [ROCK, DRIVE],
    bassPatterns: [EIGHTHS, SYNCOPATED],
    accompaniments: [DOWN, UP],
    leadDensity: 0.9,
  },
  {
    name: 'candy',
    scale: 'major',
    progressions: POP,
    lead: 'pulse25',
    arp: 'bell',
    bass: 'softSquare',
    kit: 'electro',
    grooves: [DISCO, FOUR],
    bassPatterns: [OCTAVES, EIGHTHS],
    accompaniments: [ALBERTI, 'stabs'],
    leadDensity: 0.85,
  },
  {
    name: 'neon',
    scale: 'minor',
    progressions: EPIC,
    lead: 'softSaw',
    arp: 'softSaw',
    bass: 'pulse25',
    kit: 'electro',
    grooves: [FOUR, DISCO],
    bassPatterns: [OCTAVES, SYNCOPATED],
    accompaniments: [UP, UP_DOWN],
    leadDensity: 0.85,
  },
  {
    name: 'ocean',
    scale: 'mixolydian',
    progressions: MIXO,
    lead: 'triangle',
    arp: 'triangle',
    bass: 'softSquare',
    kit: 'soft',
    grooves: [BOUNCE, HALF],
    bassPatterns: [WALK, ROOT_FIFTH],
    accompaniments: [BROKEN, UP_DOWN],
    leadDensity: 0.75,
  },
];

/**
 * A boss level's music, whatever its theme: darker and driving — the dragon's in harmonic minor
 * over rock drums, the robot's in minor with a pumping bass and electro drums.
 */
export const BOSS_STYLES: Readonly<Record<BossKind, MusicStyle>> = {
  dragon: {
    name: 'dragon',
    scale: 'harmonicMinor',
    progressions: [...HEROIC, ...EPIC],
    lead: 'softSaw',
    arp: 'pulse12',
    bass: 'softSaw',
    kit: 'chip',
    grooves: [ROCK, DRIVE],
    bassPatterns: [EIGHTHS, SYNCOPATED],
    accompaniments: [DOWN, UP],
    leadDensity: 0.9,
  },
  robot: {
    name: 'robot',
    scale: 'minor',
    progressions: EPIC,
    lead: 'pulse25',
    arp: 'softSaw',
    bass: 'pulse25',
    kit: 'electro',
    grooves: [DRIVE, FOUR],
    bassPatterns: [OCTAVES, EIGHTHS],
    accompaniments: ['stabs', UP_DOWN],
    leadDensity: 0.85,
  },
};

/** The style of a level theme (themes beyond the list wrap around). */
export function musicStyle(theme: number): MusicStyle {
  const count = Math.min(THEME_COUNT, MUSIC_STYLES.length);
  const index = ((Math.floor(theme) % count) + count) % count;
  return MUSIC_STYLES[index] ?? (MUSIC_STYLES[0] as MusicStyle);
}

// Holiday progressions. In harmonic minor the fifth degree is a major chord (its raised
// seventh pulls home), the sixth a big dark major one: i–VI–iv–V and friends.
const SPOOKY: (readonly [number, number, number, number])[] = [
  [0, 5, 3, 4],
  [0, 3, 4, 0],
  [0, 5, 0, 4],
  [0, 3, 5, 4],
];
const FESTIVE: (readonly [number, number, number, number])[] = [
  [0, 3, 4, 0],
  [0, 5, 3, 4],
  [0, 3, 1, 4],
  [3, 4, 0, 5],
];

const holidayStyles = new Map<MusicStyle, Map<Season, MusicStyle>>();

/**
 * The holiday variant of a theme's style: the same grooves and accompaniment patterns (the
 * level keeps its rhythm), other harmony and instruments.
 *  - Halloween: harmonic minor, a church organ over a celesta, a deep bell at every phrase,
 *    softer drums, a sparser melody.
 *  - Winter: a bright major key, celesta and bells, sleigh bells instead of the hi-hats.
 */
export function holidayStyle(style: MusicStyle, season: Season): MusicStyle {
  if (season === 'none') return style;
  let bySeason = holidayStyles.get(style);
  if (!bySeason) {
    bySeason = new Map();
    holidayStyles.set(style, bySeason);
  }
  let variant = bySeason.get(season);
  if (!variant) {
    variant =
      season === 'halloween'
        ? {
            ...style,
            name: `${style.name}~halloween`,
            scale: 'harmonicMinor',
            progressions: SPOOKY,
            lead: 'organ',
            arp: 'celesta',
            bass: style.bass === 'triangle' ? 'softSaw' : style.bass,
            kit: 'soft',
            leadDensity: Math.min(style.leadDensity, 0.7),
            bells: true,
          }
        : {
            ...style,
            name: `${style.name}~winter`,
            scale: 'major',
            progressions: FESTIVE,
            lead: 'celesta',
            arp: 'bell',
            bass: 'softSquare',
            kit: 'soft',
            jingle: true,
          };
    bySeason.set(season, variant);
  }
  return variant;
}
